import { createSlice } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { SharedNotesState } from 'features/shared-notes/types';
import { signalingRoomJoined } from 'features/room/actions';
import { pong } from 'features/application/actions';
import { signalingSharedNotesReceived } from 'features/shared-notes/actions';

export const initialState: SharedNotesState = {
  collabConfig: undefined,
  shouldReconnectNotes: false,
};

export const sharedNotesSlice = createSlice({
  name: 'sharedNotes',
  initialState,
  reducers: {
    resetReconnectNotesFlag: (state) => {
      state.shouldReconnectNotes = false;
    },
    collaborationConfigReset: (state) => {
      state.collabConfig = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signalingRoomJoined, (state, action) => {
        const { sharedNotes } = action.payload;
        const hadConfigBefore = !!state.collabConfig;
        const oldDocumentId = state.collabConfig?.documentId;

        if (sharedNotes) {
          state.collabConfig = {
            documentId: sharedNotes.documentId,
            serverUrl: sharedNotes.serverUrl,
            token: sharedNotes.token,
          };
        }

        // Only enable reconnection for actual network reconnections, not for room changes
        // Check if this is a legitimate room change (breakout room movement) vs reconnection
        const isRoomMovement =
          hadConfigBefore && sharedNotes && oldDocumentId !== sharedNotes.documentId;

        // Enable automatic reconnection only when:
        // 1. We had config before AND
        // 2. This is NOT a room movement (document ID change) AND
        // 3. The document ID is the same (indicating reconnection to same room)
        if (hadConfigBefore && !isRoomMovement) {
          state.shouldReconnectNotes = true;
        }
      })
      .addCase(signalingSharedNotesReceived, (state, action) => {
        const sharedNotes = action.payload;
        const hadConfigBefore = !!state.collabConfig;
        const oldDocumentId = state.collabConfig?.documentId;

        state.collabConfig = {
          documentId: sharedNotes.documentId,
          serverUrl: sharedNotes.serverUrl,
          token: sharedNotes.token,
        };

        // Only set a reconnect flag if we had a config before
        // If this is the first time receiving config, socket will initialize normally
        if (hadConfigBefore && oldDocumentId === sharedNotes.documentId) {
          state.shouldReconnectNotes = true;
        }
      })
      .addCase(pong, (state, action) => {
        if (state.collabConfig && action.payload.sharedNotesToken) {
          state.collabConfig.token = action.payload.sharedNotesToken;
        }
      });
  },
});

export const { resetReconnectNotesFlag, collaborationConfigReset } = sharedNotesSlice.actions;

export const selectSharedNotesCollabConfig = (state: RootState) => state.sharedNotes.collabConfig;
export const selectShouldReconnectNotes = (state: RootState) =>
  state.sharedNotes.shouldReconnectNotes;

export default sharedNotesSlice.reducer;
